import { getBaseUrl, apiClient } from './httpClient';
import { playAudioSafely, isIOS, isUnlocked } from './iosAudioHelper';
/**
 * SOS Audio Service
 * 
 * Service dédié pour les sons SOS (Calm et Respiration)
 * SOS Calm music is served from local backend files
 * SOS Respiration sounds are hosted on Cloudflare R2
 * 
 * ⚠️ IMPORTANT :
 * - Pas d'égaliseur audiométrique
 * - Pas de notch filter
 * - Pas de tracking de thérapie
 * - Pas de spectrogramme
 * - Sons de soutien uniquement
 * 
 * V1.5: iOS Capacitor Fix - Use apiClient for API calls and getBaseUrl() dynamically
 *       to ensure correct URL resolution on iOS native platforms.
 */

// Use dynamic getter for iOS Capacitor compatibility
const getBackendUrl = () => getBaseUrl();

/**
 * Get all SOS Calm sounds from local backend
 * These are the 5 Calm MP3 files stored locally
 * @returns {Promise<Array>} Array of sound URLs
 */
export const getSosCalmSounds = async () => {
  try {
    console.log('🎵 [SOS Calm] Loading local music tracks...');
    
    // Use apiClient for iOS-safe API calls
    const response = await apiClient.get('/api/sos-calm/music/list');
    const data = response.data;
    
    if (!data.tracks || data.tracks.length === 0) {
      console.warn('⚠️ [SOS Calm] No local music tracks found');
      return [];
    }
    
    // Get backend URL dynamically for building stream URLs
    const backendUrl = getBackendUrl();
    console.log('🎵 [SOS Calm] Using backend URL:', backendUrl);
    
    // Map local files to sound objects with streaming URLs
    const R2_AUDIO_BASE_URL =
      'https://pub-accb20ccbca0407185b856dee8a7ff7a.r2.dev';

    const sounds = data.tracks.map((track, index) => {
      const filename =
        typeof track === 'string'
          ? track
          : track.filename;

      const r2Key =
        typeof track === 'string'
          ? `sos-calm/${filename}`
          : (track.r2_key || `sos-calm/${filename}`);

      const url =
        track?.url
          ? track.url
          : `${R2_AUDIO_BASE_URL}/${r2Key}`;

      console.log('[SOS Calm][URL]', url);

      return {
        url,
        name: `SOS Calm ${index + 1}`,
        filename,
        category: 'sos_calm'
      };
    });
    
    console.log(`✅ [SOS Calm] Loaded ${sounds.length} local music tracks`);
    sounds.forEach(sound => {
      console.log(`  📄 ${sound.filename}: ${sound.url}`);
    });
    
    return sounds;
  } catch (error) {
    console.error('❌ Error loading SOS Calm sounds:', error);
    console.error('❌ Error details:', error.message);
    return [];
  }
};

/**
 * Get all SOS Respiration sounds from local backend
 * These are the 10 Respiration MP3 files stored locally for Advanced Breathing
 * @returns {Promise<Array>} Array of sound URLs
 */
export const getSosRespirationSounds = async () => {
  try {
    console.log('🫁 [SOS Respiration] Loading local music tracks...');
    
    // Use apiClient for iOS-safe API calls
    const response = await apiClient.get('/api/respiration/music/list');
    const data = response.data;
    
    if (!data.tracks || data.tracks.length === 0) {
      console.warn('⚠️ [SOS Respiration] No local music tracks found');
      return [];
    }
    
    const R2_AUDIO_BASE_URL =
      'https://pub-accb20ccbca0407185b856dee8a7ff7a.r2.dev';

    console.log('🫁 [SOS Respiration] Using R2 direct URL');

    const sounds = data.tracks.map((track, index) => {
      const filename = typeof track === 'string' ? track : track.filename;
      const r2Key = typeof track === 'string'
        ? `respiration/${filename}`
        : (track.r2_key || `respiration/${filename}`);

      return {
        url: `${R2_AUDIO_BASE_URL}/${r2Key}`,
        name: `SOS Respiration ${index + 1}`,
        filename,
        category: 'sos_respiration'
      };
    });
    
    console.log(`✅ [SOS Respiration] Loaded ${sounds.length} local music tracks`);
    sounds.forEach(sound => {
      console.log(`  📄 ${sound.filename}: ${sound.url}`);
    });
    
    return sounds;
  } catch (error) {
    console.error('❌ Error loading SOS Respiration sounds:', error);
    console.error('❌ Error details:', error.message);
    return [];
  }
};

/**
 * Get a random SOS Calm sound
 * @returns {Promise<Object>} Random sound object
 */
export const getRandomSosCalmSound = async () => {
  const sounds = await getSosCalmSounds();
  if (sounds.length === 0) return null;
  
  const randomIndex = Math.floor(Math.random() * sounds.length);
  return sounds[randomIndex];
};

/**
 * Get a random SOS Respiration sound
 * @returns {Promise<Object>} Random sound object
 */
export const getRandomSosRespirationSound = async () => {
  const sounds = await getSosRespirationSounds();
  if (sounds.length === 0) return null;
  
  const randomIndex = Math.floor(Math.random() * sounds.length);
  return sounds[randomIndex];
};

/**
 * Play a SOS sound (simple HTML5 Audio)
 * No therapy tracking, no EQ, no notch
 * V1.4: Uses iOS-safe play with instrumentation
 * @param {string} url - Sound URL
 * @param {number} volume - Volume (0-1)
 * @returns {HTMLAudioElement} Audio element
 */
export const playSosSound = async (url, volume = 0.3) => {
  const audio = new Audio(url);
  audio.volume = volume;
  audio.loop = false;
  
  // Warn if iOS and not unlocked
  if (isIOS() && !isUnlocked()) {
    console.warn('[SOS Audio] ⚠️ iOS: Audio may not be unlocked yet');
  }
  
  // Use safe play with instrumentation
  const success = await playAudioSafely(audio, `SOS: ${url.split('/').pop()}`);
  
  if (!success) {
    console.error('[SOS Audio] ❌ Failed to play sound');
  }
  
  return audio;
};

/**
 * Play a looping SOS sound (for background music)
 * @param {string} url - Sound URL
 * @param {number} volume - Volume (0-1)
 * @returns {HTMLAudioElement} Audio element
 */
export const playSosSoundLoop = (url, volume = 0.2) => {
  const audio = new Audio(url);
  audio.volume = volume;
  audio.loop = true;
  
  audio.play().catch(error => {
    console.error('❌ Error playing looping SOS sound:', error);
  });
  
  return audio;
};

export default {
  getSosCalmSounds,
  getSosRespirationSounds,
  getRandomSosCalmSound,
  getRandomSosRespirationSound,
  playSosSound,
  playSosSoundLoop
};
